import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import "echarts-wordcloud";

import LoadingBlock from "../LoadingBlock/LoadingBlock";

function colorFromTextHSL(text) {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

function readCssVar(name, fallback) {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return (v || "").trim() || fallback;
}

export default function TagsWordcloudChart({
  data = [],
  loading = false,
  title = "Top 10 - Nuvem de assuntos",
  filename = "top-10-nuvem-de-assuntos",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const CARD_MIN_H = 360;
  const CHART_H = 320;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const option = useMemo(() => {
    const bg = "transparent";
    const titleColor = "#614a9e";

    const tooltipBg = readCssVar("--bottom-dropdown-color", "#FFFFFF");
    const tooltipBorder = "rgba(0,0,0,0.12)";
    const textColor = readCssVar("--text-color", "#111827");

    return {
      backgroundColor: bg,

      title: {
        text: exportMode ? "" : title,
        left: 12,
        top: 10,
        textStyle: {
          fontSize: 14,
          fontWeight: 800,
          color: titleColor,
        },
      },

      toolbox: { show: false },

      tooltip: {
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        borderWidth: 1,
        textStyle: { color: textColor },
        formatter: (p) => `${p.name}<br/><b>${p.value}</b> notícias`,
      },

      series: [
        {
          type: "wordCloud",
          shape: "rect",
          rotationRange: [0, 90],
          rotationStep: 90,
          sizeRange: exportMode ? [14, 34] : [10, 22],
          gridSize: exportMode ? 1 : 2,
          left: 0,
          top: 0,
          width: "100%",
          height: "100%",

          textStyle: {
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: "600",
            color: (p) => colorFromTextHSL(p.name),
          },

          emphasis: {
            textStyle: {
              shadowBlur: 10,
              shadowColor: "rgba(0,0,0,0.25)",
            },
          },

          data: (data || []).map((x) => ({
            name: x.name,
            value: x.weight,
          })),
        },
      ],
    };
  }, [data, title, exportMode]);

  useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    const t = setTimeout(() => {
      try {
        instance.resize();
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, data]);

  const handleExportPNG = () => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;

    const bg = readCssVar("--background-graphic", "#FFFFFF");

    const url = instance.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: bg,
      excludeComponents: ["toolbox"],
    });

    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.png`;
    a.click();
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando assuntos...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <ReactECharts
          key={chartKey}
          ref={chartRef}
          option={option}
          notMerge={false}
          lazyUpdate={true}
          style={{ height: exportMode ? 560 : CHART_H, width: "100%" }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
